import { commandKeys } from '../../../mqttData/commands/commands.enum';
import { SpaLiveLabels } from '../../../mqttData/live/spaLiveLabels.enums';
import { SpaSettingsLabels } from '../../../mqttData/settings/spaSettingsLabels';
import { PEAK_FIELDS } from '../../../pages/SpaControl/PeakManagement/economyMode';
import { peakScreenToV1 } from './v1PeakAdapter';
import { uiToSpeed } from './v1Transforms';

/**
 * The portal's V2 command vocabulary → what a V1 / Coldfire spa understands.
 *
 * Every V1 write goes to one topic, `arctic/spa/{guid}/controls/spa`, as a JSON envelope
 * with exactly one wrapper key (`spaCommand`, `spaSettings`, `spaboySettings` or
 * `peak`) that the legacy SpaServer unwraps and forwards to the pack. Where V2 sends
 * "next" toggles (`P1next`, `Linext`), V1 wants explicit wire values, so the
 * translation needs the current live state to decide what "next" is. Field names and
 * values follow the mobile app's `V1Connection` / `SpaControlScreen.updateV1Control`
 * and the legacy portal's `MQTT/json/*.cs`.
 */

export type V1PublishKind = 'command' | 'settings' | 'spaboySettings' | 'peak';

export interface V1Publish {
  kind: V1PublishKind;
  body: Record<string, any>;
}

type Snapshot = Record<string, any>;

const warnUnsupported = (what: string) =>
  console.warn(`[V1] "${what}" has no equivalent on a V1 spa and was not sent`);

/**
 * `Command.executeSingle(key, value)` for a V1 spa.
 *
 * @param live the current `live` slice, used to work out the next state of a toggle
 */
export function translateCommand(key: string, value: any, live: Snapshot = {}): V1Publish[] {
  const toggle = (label: string, onValue = 1) => (live[label] ? 0 : onValue);
  const cmd = (body: Record<string, any>): V1Publish[] => [{ kind: 'command', body }];

  switch (key) {
    case commandKeys.P1Next: {
      // Pump 1 cycles off → low → high → off (0 / 15 / 100 on the portal's scale).
      const cur = Number(live[SpaLiveLabels.Pump1]) || 0;
      const nextUi = cur === 0 ? 15 : cur <= 15 ? 100 : 0;
      return cmd({ pump1: uiToSpeed(nextUi) });
    }
    // The other pumps are off → high → off, as the legacy portal cycled them.
    case commandKeys.P2Next: return cmd({ pump2: toggle(SpaLiveLabels.Pump2, 2) });
    case commandKeys.P3Next: return cmd({ pump3: toggle(SpaLiveLabels.Pump3, 2) });
    case commandKeys.P4Next: return cmd({ pump4: toggle(SpaLiveLabels.Pump4, 2) });
    case commandKeys.P5Next: return cmd({ pump5: toggle(SpaLiveLabels.Pump5, 2) });
    // Blowers take a 0/1/2 speed too; the apps only offer on/off, so on is speed 1.
    case commandKeys.Bl1Next: return cmd({ blower1: toggle(SpaLiveLabels.BL1) });
    case commandKeys.BL2Next: return cmd({ blower2: toggle(SpaLiveLabels.BL2) });
    case commandKeys.LightsNext: return cmd({ lights: toggle(SpaLiveLabels.Lights) });
    case commandKeys.SDSNext: return cmd({ sds: toggle(SpaLiveLabels.SDS) });
    case commandKeys.YESSNext: return cmd({ yess: toggle(SpaLiveLabels.Yess) });
    case commandKeys.FgNext: return cmd({ fogger: toggle(SpaLiveLabels.Fogger) });
    case commandKeys.OnzNext: return cmd({ onzen: toggle(SpaLiveLabels.On) });
    // The manual filter cycle and the SpaBoy boost are plain spa commands on V1.
    case commandKeys.FLTRBoost: return cmd({ filter: value ?? 1 });
    case commandKeys.SBBoost: return cmd({ boost: value ?? 1 });
    // A Coldfire has one reset; the legacy portal sends it as spaCommand{reset:true}.
    case commandKeys.Reset: return cmd({ reset: true });
    default:
      warnUnsupported(key);
      return [];
  }
}

/** Settings keys whose V1 counterpart is a `{min,max,current}` range on spaSettings. */
const RANGE_SETTINGS: Record<string, string> = {
  [SpaSettingsLabels.setFD]: 'filterDuration',
  [SpaSettingsLabels.setFF]: 'filterFrequency',
  [SpaSettingsLabels.setOzCy]: 'ozoneCycles',
  [SpaSettingsLabels.setOzHr]: 'ozoneHours',
  [SpaSettingsLabels.setOnHr]: 'onzenHours',
  [SpaSettingsLabels.setOnCy]: 'onzenCycles',
};

const PEAK_KEYS = new Set(PEAK_FIELDS);

/**
 * `Command.updateSettings(params)` for a V1 spa. One V2 payload may fan out to several
 * envelopes: the setpoint is a spa command, filtration / sanitation are spa settings,
 * ORP is a SpaBoy setting and the tariff schedule is a full `peak` message.
 *
 * @param settings the current `settings` slice, to complete partial pairs (ORP lo/hi)
 *   and to send the whole schedule when the page only changed part of it
 */
export function translateSettings(params: Snapshot, settings: Snapshot = {}): V1Publish[] {
  if (!params || typeof params !== 'object') return [];
  const out: V1Publish[] = [];
  const command: Record<string, any> = {};
  const spaSettings: Record<string, any> = {};
  const spaboy: Record<string, any> = {};
  let peakTouched = false;
  const unsupported: string[] = [];

  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value === undefined) return;

    if (key === SpaSettingsLabels.setTSP) {
      // Set points are always sent in °F, as a spa command.
      command.setpoint = Number(value);
      return;
    }
    if (RANGE_SETTINGS[key]) {
      spaSettings[RANGE_SETTINGS[key]] = { current: Number(value) };
      return;
    }
    // The Filtration page writes `setFS`; the reading key is `FS`. Accept both.
    if (key === 'setFS' || key === SpaSettingsLabels.FS) {
      spaSettings.filterSuspension = value === true || Number(value) > 0;
      return;
    }
    if (key === 'setSBHrs') {
      spaSettings.spaboyHours = Number(value);
      return;
    }
    if (key === SpaSettingsLabels.cfgToff) {
      spaSettings.tempOffset = Number(value);
      return;
    }
    if (key === SpaSettingsLabels.SBORPlo || key === SpaSettingsLabels.SBORPhi) {
      spaboy.orp = spaboy.orp || {};
      spaboy.orp[key === SpaSettingsLabels.SBORPlo ? 'min' : 'max'] = Number(value);
      return;
    }
    if (key === SpaSettingsLabels.SBpHlo || key === SpaSettingsLabels.SBpHhi) {
      spaboy.ph = spaboy.ph || {};
      spaboy.ph[key === SpaSettingsLabels.SBpHlo ? 'min' : 'max'] = Number(value);
      return;
    }
    if (PEAK_KEYS.has(key)) {
      peakTouched = true;
      return;
    }
    unsupported.push(key);
  });

  // The pack wants both ends of a range; fill the untouched end from what it last reported.
  if (spaboy.orp) {
    if (spaboy.orp.min === undefined && settings[SpaSettingsLabels.SBORPlo] != null) spaboy.orp.min = Number(settings[SpaSettingsLabels.SBORPlo]);
    if (spaboy.orp.max === undefined && settings[SpaSettingsLabels.SBORPhi] != null) spaboy.orp.max = Number(settings[SpaSettingsLabels.SBORPhi]);
  }
  if (spaboy.ph) {
    if (spaboy.ph.min === undefined && settings[SpaSettingsLabels.SBpHlo] != null) spaboy.ph.min = Number(settings[SpaSettingsLabels.SBpHlo]);
    if (spaboy.ph.max === undefined && settings[SpaSettingsLabels.SBpHhi] != null) spaboy.ph.max = Number(settings[SpaSettingsLabels.SBpHhi]);
  }

  if (Object.keys(command).length) out.push({ kind: 'command', body: command });
  if (Object.keys(spaSettings).length) out.push({ kind: 'settings', body: spaSettings });
  if (Object.keys(spaboy).length) out.push({ kind: 'spaboySettings', body: spaboy });
  if (peakTouched) {
    // Coldfire packs refuse a partial schedule, so the whole thing goes out every time.
    const merged: Snapshot = {};
    PEAK_FIELDS.forEach((k) => {
      if (settings[k] !== undefined) merged[k] = settings[k];
    });
    PEAK_FIELDS.forEach((k) => {
      if (params[k] !== undefined) merged[k] = params[k];
    });
    out.push({ kind: 'peak', body: peakScreenToV1(merged) });
  }

  if (unsupported.length) warnUnsupported(unsupported.join(', '));
  return out;
}
